import { MonetaryAmount, Shimmer } from '@components'
import type { ReconciliationAccount, ReportAttachmentsByDocument } from '@query'
import { reportAttachmentsFor } from '@query'
import { formatDate } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { EntryAttachmentAction } from './EntryAttachmentAction'
import { EntryPanelPagination } from './EntryPanelPagination'
import { ReconciliationEntryShimmerRows } from './EntryPanelShimmer'
import { ReportDocumentPreview } from './ReportDocumentPreview'
import {
  AttachmentSlot,
  DateCell,
  EntryHeaderRow,
  EntryPanel,
  EntryVoucherDescription,
  EntryVoucherNumber,
  EntryVoucherRow,
  ReportRow,
  RowLabel,
  ValueCell
} from './interactive.styled'

type Props = {
  account: ReconciliationAccount
  attachmentsByDocument?: ReportAttachmentsByDocument
  currency?: string
  isLoading?: boolean
  entriesPage: number
  entriesPageCount: number
  entriesTotalCount: number
  entriesPageSize?: number
  onEntriesPageChange: (page: number) => void
}

export const ReconciliationEntriesPanel: React.FC<Props> = ({
  account,
  attachmentsByDocument,
  currency,
  isLoading = false,
  entriesPage,
  entriesPageCount,
  entriesTotalCount,
  entriesPageSize,
  onEntriesPageChange
}) => {
  const [t] = useTranslation()
  const entries = [...account.entries].reverse()
  const showPagination = entriesTotalCount > 0

  return (
    <EntryPanel>
      <EntryHeaderRow>
        <AttachmentSlot />
        <DateCell $header>{t('reports.interactive.date')}</DateCell>
        <RowLabel $header>{t('reports.interactive.voucherDescription')}</RowLabel>
        <ValueCell $width="128px" $header>
          {t('reports.interactive.amount')}
        </ValueCell>
      </EntryHeaderRow>

      <ReportRow $entryRow $bg="#f7fafd">
        <AttachmentSlot />
        <DateCell />
        <EntryVoucherRow>
          <EntryVoucherDescription>
            {t('reports.interactive.openingBalance')}
          </EntryVoucherDescription>
        </EntryVoucherRow>
        <ValueCell $width="128px" $muted>
          {isLoading ? (
            <Shimmer width={72} height={12} borderRadius={4} />
          ) : (
            <MonetaryAmount value={account.opening_balance} currency={currency} />
          )}
        </ValueCell>
      </ReportRow>

      {isLoading ? (
        <ReconciliationEntryShimmerRows />
      ) : (
        entries.map((entry, index) => (
          <ReportRow key={`${entry.number}-${index}`} $entryRow $bg="#f7fafd">
            <EntryAttachmentAction
              attachments={reportAttachmentsFor(attachmentsByDocument, entry.document_id)}
            />
            <DateCell>{formatDate(entry.date)}</DateCell>
            <EntryVoucherRow>
              <EntryVoucherNumber>
                {entry.document_id ? (
                  <ReportDocumentPreview documentId={entry.document_id}>
                    {entry.number}
                  </ReportDocumentPreview>
                ) : (
                  entry.number
                )}
              </EntryVoucherNumber>
              {entry.description ? (
                <EntryVoucherDescription>{entry.description}</EntryVoucherDescription>
              ) : null}
            </EntryVoucherRow>
            <ValueCell $width="128px" $muted>
              <MonetaryAmount value={entry.amount} currency={currency} />
            </ValueCell>
          </ReportRow>
        ))
      )}

      {showPagination && (
        <EntryPanelPagination
          page={entriesPage}
          pageCount={entriesPageCount}
          totalCount={entriesTotalCount}
          pageSize={entriesPageSize}
          onPageChange={onEntriesPageChange}
        />
      )}
    </EntryPanel>
  )
}
